/**
 * Page `/saisie` — vue dédiée aux bénévoles caisse / accueil le jour J.
 *
 * Flux nominal :
 *   1. Jeton caisse (`sqseverac.saisieToken`) **ou** jeton admin (`sqseverac.admin.token`)
 *      — l'API accepte les deux.
 *   2. Recherche d'une préréservation par nom / prénom / email.
 *   3. Encaissement partiel (+X ad / +Y enf) si besoin.
 *   4. Remise de bracelets physiques (adulte / enfant) ≤ repas payés ;
 *      décompte partagé entre postes (même API).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import WristIcon from '@mui/icons-material/Watch';
import {
  cancelPayment,
  createPayment,
  extractApiError,
  fetchVolunteerMe,
  handoffBracelets,
  listPayments,
  saisieGetOne,
  saisieSearch,
  setBracelets,
} from '../api.js';
import { formatReservationEmailDisplay } from '../reservationEmailDisplay.js';
import {
  clearVolunteerSession,
  getSaisieAuthHeaders,
  getVolunteerSession,
  notifyRolesChanged,
  ROLES_CHANGED_EVENT,
  STORAGE_KEYS,
  updateVolunteerMetaFromMe,
} from '../useActiveRoles.js';
import StaffEmailLogin from '../components/StaffEmailLogin.jsx';

const SAISIE_TOKEN_KEY = STORAGE_KEYS.saisie;

function hasSaisieAuth(headers) {
  return Boolean(headers && Object.keys(headers).length);
}

const PAYMENT_METHODS = [
  { value: 'cash',     label: 'Espèces' },
  { value: 'check',    label: 'Chèque' },
  { value: 'card',     label: 'Carte bancaire' },
  { value: 'transfer', label: 'Virement' },
  { value: 'other',    label: 'Autre' },
];
const METHOD_LABEL = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));

/**
 * `true` si la résa est soldée (= confirmée, solde nul, au moins 1 c reçu).
 * Mutualisé entre `ResultRow` et le filtre liste pour ne jamais diverger.
 */
function isFullyPaid(row) {
  if (!row) return false;
  const balance =
    row.balance_cents ?? Math.max(0, (row.due_cents ?? 0) - (row.paid_cents ?? 0));
  return Boolean(row.confirmed_at) && balance === 0 && (row.paid_cents ?? 0) > 0;
}

function braceletsRemaining(row) {
  if (!row) return { adults: 0, children: 0, total: 0 };
  const adults = row.bracelets_adults_remaining
    ?? Math.max(0, (row.paid_adults ?? 0) - (row.bracelets_adults_given ?? 0));
  const children = row.bracelets_children_remaining
    ?? Math.max(0, (row.paid_children ?? 0) - (row.bracelets_children_given ?? 0));
  return { adults, children, total: adults + children };
}

function needsPayment(row) {
  return Boolean(row) && !isFullyPaid(row);
}

function needsBracelets(row) {
  return braceletsRemaining(row).total > 0;
}

/** Mode poste caisse — mémorisé par navigateur (chaque PC garde son rôle). */
const LS_STATION_MODE = 'sqseverac.saisie.stationMode';
function needsAction(row) {
  return needsPayment(row) || needsBracelets(row);
}

/** Résa terminée côté accueil : soldée et tous bracelets remis. */
function isClosedAtDesk(row) {
  return isFullyPaid(row) && !needsBracelets(row);
}

const STATION_MODES = [
  {
    id: 'encaissement',
    label: 'Encaissement',
    hint: 'Résas avec solde à payer',
    iconMode: 'paid',
  },
  {
    id: 'bracelets',
    label: 'Bracelets',
    hint: 'Repas payés, bracelets à remettre',
    iconMode: 'wrist',
  },
  {
    id: 'combo',
    label: null, // rendu custom : icônes paiement + bracelets
    hint: 'À encaisser ou bracelets à remettre (masque les clôturées)',
    iconMode: 'combo',
  },
  {
    id: 'all',
    label: 'Tout',
    hint: 'Y compris les résas clôturées (soldées + bracelets remis)',
    iconMode: null,
  },
];

function sortByName(a, b) {
  const ln = String(a.last_name || '').localeCompare(String(b.last_name || ''), 'fr', {
    sensitivity: 'base',
  });
  if (ln !== 0) return ln;
  return String(a.first_name || '').localeCompare(String(b.first_name || ''), 'fr', {
    sensitivity: 'base',
  });
}

function formatEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function Saisie() {
  const location = useLocation();

  // -------- Authentification --------
  const [authHeaders, setAuthHeaders] = useState(getSaisieAuthHeaders);
  const token = authHeaders; // compat : les appels API acceptent string ou headers
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');

  function handleLogin(e) {
    e?.preventDefault();
    const t = tokenInput.trim();
    if (t.length < 8) {
      setAuthError('Jeton trop court (8 caractères minimum).');
      return;
    }
    localStorage.setItem(SAISIE_TOKEN_KEY, t);
    notifyRolesChanged();
    setAuthHeaders(getSaisieAuthHeaders());
    setAuthError('');
    setTokenInput('');
  }

  function handleLogout() {
    localStorage.removeItem(SAISIE_TOKEN_KEY);
    localStorage.removeItem(STORAGE_KEYS.admin);
    if (getVolunteerSession()) clearVolunteerSession();
    else notifyRolesChanged();
    setAuthHeaders(getSaisieAuthHeaders());
    setSelected(null);
    setResults([]);
  }

  const syncTokenFromStorage = useCallback(() => {
    setAuthHeaders(getSaisieAuthHeaders());
  }, []);

  useEffect(() => {
    syncTokenFromStorage();
  }, [location.pathname, syncTokenFromStorage]);

  useEffect(() => {
    const onExternal = () => syncTokenFromStorage();
    window.addEventListener('storage', onExternal);
    window.addEventListener('focus', onExternal);
    window.addEventListener(ROLES_CHANGED_EVENT, onExternal);
    return () => {
      window.removeEventListener('storage', onExternal);
      window.removeEventListener('focus', onExternal);
      window.removeEventListener(ROLES_CHANGED_EVENT, onExternal);
    };
  }, [syncTokenFromStorage]);

  const isAuth = hasSaisieAuth(authHeaders);

  // Droits staff_access éventuellement mis à jour par le super-admin.
  useEffect(() => {
    const session = getVolunteerSession();
    if (!session || isAuth) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchVolunteerMe(session);
        if (cancelled) return;
        updateVolunteerMetaFromMe(me);
        setAuthHeaders(getSaisieAuthHeaders());
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuth]);

  // -------- Recherche --------
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  // Mode poste (encaissement / bracelets / tout) — persistant par navigateur.
  const [stationMode, setStationMode] = useState(() => {
    try {
      const v = localStorage.getItem(LS_STATION_MODE);
      if (v === 'encaissement' || v === 'bracelets' || v === 'combo' || v === 'all') return v;
    } catch { /* ignore */ }
    return 'combo';
  });
  function changeStationMode(mode) {
    setStationMode(mode);
    try { localStorage.setItem(LS_STATION_MODE, mode); } catch { /* ignore */ }
  }

  const counts = useMemo(() => {
    const list = Array.isArray(results) ? results : [];
    return {
      encaissement: list.filter(needsPayment).length,
      bracelets: list.filter(needsBracelets).length,
      combo: list.filter(needsAction).length,
      all: list.length,
    };
  }, [results]);

  const displayedResults = useMemo(() => {
    const base = Array.isArray(results) ? results : [];
    let list = base;
    if (stationMode === 'encaissement') list = base.filter(needsPayment);
    else if (stationMode === 'bracelets') list = base.filter(needsBracelets);
    else if (stationMode === 'combo') list = base.filter(needsAction);
    // 'all' : y compris clôturées
    return [...list].sort(sortByName);
  }, [results, stationMode]);

  const emptyListHint = useMemo(() => {
    const len = Array.isArray(results) ? results.length : 0;
    if (len === 0) return 'Aucun résultat.';
    if (stationMode === 'encaissement') {
      return counts.encaissement === 0
        ? 'Aucune réservation à encaisser (filtre Encaissement).'
        : null;
    }
    if (stationMode === 'bracelets') {
      return counts.bracelets === 0
        ? 'Aucun bracelet à remettre (filtre Bracelets).'
        : null;
    }
    if (stationMode === 'combo') {
      return counts.combo === 0
        ? 'Rien à traiter (tout est encaissé et bracelets remis).'
        : null;
    }
    return null;
  }, [results, stationMode, counts]);

  const search = useCallback(async (q) => {
    if (!hasSaisieAuth(token)) return;
    setSearching(true);
    setSearchError('');
    try {
      const list = await saisieSearch(token, q);
      setResults(Array.isArray(list) ? list : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        setAuthError('Jeton refusé.');
        return;
      }
      setSearchError(extractApiError(err, 'Recherche impossible.'));
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [token]);

  useEffect(() => { if (isAuth) search(''); }, [isAuth, search]);
  useEffect(() => {
    if (!isAuth) return;
    const handle = setTimeout(() => { search(query); }, 250);
    return () => clearTimeout(handle);
  }, [query, isAuth, search]);

  // -------- Sélection / encaissement --------
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);

  /** Recharge la fiche + l'historique des paiements à la demande. */
  const reloadSelected = useCallback(
    async (id) => {
      if (!id) return;
      const [fresh, history] = await Promise.all([
        saisieGetOne(token, id),
        listPayments(token, id),
      ]);
      setSelected(fresh);
      setPayments(history);
    },
    [token],
  );

  async function openOne(row) {
    try {
      await reloadSelected(row.id);
    } catch (err) {
      setSearchError(extractApiError(err, 'Fiche inaccessible.'));
    }
  }

  return isAuth ? (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <BackToHome />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaidOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Caisse / Accueil
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Choisissez le type de poste : la liste se filtre automatiquement.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Se déconnecter">
          <IconButton onClick={handleLogout} aria-label="Se déconnecter">
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {STATION_MODES.map((m) => {
          const selected = stationMode === m.id;
          const count = counts[m.id];
          const countSuffix = count != null ? ` (${count})` : '';
          const label = m.iconMode === 'combo' ? (
            <Stack direction="row" alignItems="center" spacing={0.5} component="span">
              <PaidOutlinedIcon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>+</Box>
              <WristIcon sx={{ fontSize: 18 }} />
              <Box component="span">{countSuffix}</Box>
            </Stack>
          ) : (
            `${m.label}${countSuffix}`
          );
          return (
            <Tooltip key={m.id} title={m.hint}>
              <Chip
                clickable
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => changeStationMode(m.id)}
                icon={
                  m.iconMode === 'wrist' ? <WristIcon />
                    : m.iconMode === 'paid' ? <PaidOutlinedIcon />
                      : undefined
                }
                label={label}
                aria-label={m.hint}
              />
            </Tooltip>
          );
        })}
      </Stack>

      <TextField
        fullWidth
        placeholder="Rechercher par nom, prénom ou email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      {searchError && <Alert severity="warning" sx={{ mb: 1 }}>{searchError}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.6fr' },
          gap: 2,
        }}
      >
        {/* Colonne gauche : résultats de recherche */}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {searching && (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!searching && displayedResults.length === 0 && (
            <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
              {emptyListHint || 'Aucun résultat pour ce filtre.'}
            </Box>
          )}
          <Stack divider={<Divider />}>
            {displayedResults.map((r) => (
              <ResultRow
                key={r.id}
                row={r}
                selected={selected?.id === r.id}
                onClick={() => openOne(r)}
              />
            ))}
          </Stack>
        </Paper>

        {/* Colonne droite : fiche détaillée + encaissements */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {!selected ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {stationMode === 'bracelets'
                ? 'Sélectionnez une réservation pour remettre les bracelets.'
                : stationMode === 'combo'
                  ? 'Sélectionnez une réservation à encaisser et/ou pour remettre les bracelets.'
                  : 'Sélectionnez une préréservation à gauche pour l’encaisser.'}
            </Typography>
          ) : (
            <FicheDetail
              row={selected}
              payments={payments}
              token={token}
              stationMode={stationMode}
              onChanged={() => {
                reloadSelected(selected.id);
                search(query);
              }}
            />
          )}
        </Paper>
      </Box>
    </Container>
  ) : (
    // ---- Login ------------------------------------------------------------
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <BackToHome />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <PaidOutlinedIcon color="primary" />
        </Stack>
        <StaffEmailLogin
          audience="saisie"
          title="Caisse / Accueil"
          description="Connectez-vous avec votre email bénévole. Après validation et attribution du droit « Caisse » par le super-admin, le navigateur garde la session automatiquement."
          legacyLabel="Jeton SAISIE_TOKEN / ADMIN"
          legacyError={authError}
          onLegacyToken={async (t) => {
            setTokenInput(t);
            if (t.length < 8) {
              setAuthError('Jeton trop court (8 caractères minimum).');
              return;
            }
            localStorage.setItem(SAISIE_TOKEN_KEY, t);
            notifyRolesChanged();
            setAuthHeaders(getSaisieAuthHeaders());
            setAuthError('');
          }}
        />
      </Paper>
    </Container>
  );
}

// ---------------------------------------------------------------------------
//  Sous-composants
// ---------------------------------------------------------------------------

function BackToHome() {
  return (
    <Tooltip title="Retour à l'accueil">
      <IconButton
        component={RouterLink}
        to="/"
        size="small"
        aria-label="Retour à l'accueil"
        sx={{ mb: 1, ml: -1 }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );
}

/** Ligne cliquable dans la liste de gauche (résumé statut). */
function ResultRow({ row, selected, onClick }) {
  const isConfirmed = Boolean(row.confirmed_at);
  const balance = row.balance_cents ?? Math.max(0, (row.due_cents ?? 0) - (row.paid_cents ?? 0));
  const fullyPaid = isFullyPaid(row);
  const paidMeals = (row.paid_adults ?? 0) + (row.paid_children ?? 0);
  const br = braceletsRemaining(row);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.25,
        cursor: 'pointer',
        backgroundColor: selected ? 'action.selected' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {row.last_name} {row.first_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatReservationEmailDisplay(row.email)} · {paidMeals}/{row.meals} repas réglés
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          {br.total > 0 && (
            <Chip size="small" icon={<WristIcon />} label={`${br.total}`} color="info" variant="outlined" />
          )}
          {isClosedAtDesk(row) ? (
            <Chip size="small" label="Clôturée" color="success" variant="outlined" />
          ) : fullyPaid ? (
            <Chip size="small" label="Réglé" color="success" />
          ) : balance > 0 && isConfirmed ? (
            <Chip size="small" label={`Reste ${formatEur(balance)}`} color="warning" variant="outlined" />
          ) : (
            <Chip size="small" label={`À payer ${formatEur(row.due_cents)}`} variant="outlined" />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Fiche détaillée + encaissement partiel + remise bracelets + historique.
 */
function FicheDetail({ row, payments, token, stationMode = 'all', onChanged }) {
  const due = row.due_cents ?? 0;
  const paid = row.paid_cents ?? 0;
  const balance = Math.max(0, due - paid);
  const isConfirmed = Boolean(row.confirmed_at);

  // Repas restant à encaisser (plafonds pour les steppers).
  const remainAdults = Math.max(0, (row.adults ?? 0) - (row.paid_adults ?? 0));
  const remainChildren = Math.max(0, (row.children ?? 0) - (row.paid_children ?? 0));
  const braceletsRemainAdults = row.bracelets_adults_remaining
    ?? Math.max(0, (row.paid_adults ?? 0) - (row.bracelets_adults_given ?? 0));
  const braceletsRemainChildren = row.bracelets_children_remaining
    ?? Math.max(0, (row.paid_children ?? 0) - (row.bracelets_children_given ?? 0));

  // Steppers locaux : par défaut on pré-remplit avec le reste à payer / remettre.
  const [addAdults, setAddAdults] = useState(remainAdults);
  const [addChildren, setAddChildren] = useState(remainChildren);
  const [giveAdults, setGiveAdults] = useState(braceletsRemainAdults);
  const [giveChildren, setGiveChildren] = useState(braceletsRemainChildren);
  const [method, setMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [overrideEur, setOverrideEur] = useState(''); // vide = montant calculé auto
  const [saving, setSaving] = useState(false);
  const [braceletSaving, setBraceletSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Resync les steppers quand on change de fiche ou que paid_* / remis évoluent.
  useEffect(() => {
    setAddAdults(remainAdults);
    setAddChildren(remainChildren);
    setGiveAdults(braceletsRemainAdults);
    setGiveChildren(braceletsRemainChildren);
    setOverrideEur('');
    setError('');
    setSuccess('');
  }, [row.id, remainAdults, remainChildren, braceletsRemainAdults, braceletsRemainChildren]);

  const computedCents = useMemo(
    () => addAdults * (row.adult_cents ?? 0) + addChildren * (row.child_cents ?? 0),
    [addAdults, addChildren, row.adult_cents, row.child_cents],
  );
  const effectiveCents = useMemo(() => {
    if (overrideEur.trim() === '') return computedCents;
    const v = Number(overrideEur.replace(',', '.'));
    return Number.isFinite(v) ? Math.round(v * 100) : computedCents;
  }, [overrideEur, computedCents]);

  async function handleSubmit(e) {
    e?.preventDefault();
    if (addAdults === 0 && addChildren === 0) {
      setError("Précisez au moins 1 adulte ou 1 enfant à encaisser.");
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      const created = await createPayment(token, row.id, {
        adults: addAdults,
        children: addChildren,
        paymentMethod: method,
        paymentNotes: notes,
        amountOverrideEur:
          overrideEur.trim() === '' ? undefined : Number(overrideEur.replace(',', '.')),
      });
      setSuccess(
        `Encaissé : ${formatEur(created.amount_cents)} (${created.adults} ad / ${created.children} enf). Remettez les bracelets correspondants.`,
      );
      // Préremplir la remise avec le juste encaissé (plafonné au reste après refresh).
      setGiveAdults(created.adults);
      setGiveChildren(created.children);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, 'Encaissement refusé.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleHandoffBracelets(e) {
    e?.preventDefault();
    if (giveAdults === 0 && giveChildren === 0) {
      setError('Précisez au moins 1 bracelet à remettre.');
      return;
    }
    setBraceletSaving(true); setError(''); setSuccess('');
    try {
      await handoffBracelets(token, row.id, {
        adults: giveAdults,
        children: giveChildren,
      });
      setSuccess(
        `Bracelets remis : ${giveAdults} adulte(s) / ${giveChildren} enfant(s).`,
      );
      onChanged();
    } catch (err) {
      setError(extractApiError(err, 'Remise de bracelets refusée.'));
    } finally {
      setBraceletSaving(false);
    }
  }

  async function handleCorrectBracelets() {
    const msg = `Corriger le cumul bracelets remis ?\n\nValeurs actuelles : ${row.bracelets_adults_given ?? 0} ad / ${row.bracelets_children_given ?? 0} enf.\nEntrez les nouveaux totaux (absolus), séparés par une virgule (ex. 2,1).`;
    const raw = window.prompt(msg, `${row.bracelets_adults_given ?? 0},${row.bracelets_children_given ?? 0}`);
    if (raw == null) return;
    const parts = raw.split(/[,;/]/).map((s) => s.trim());
    const adults = Number(parts[0]);
    const children = Number(parts[1] ?? 0);
    if (!Number.isInteger(adults) || !Number.isInteger(children) || adults < 0 || children < 0) {
      setError('Correction invalide (attendu : adultes,enfants).');
      return;
    }
    setBraceletSaving(true); setError(''); setSuccess('');
    try {
      await setBracelets(token, row.id, { adults, children });
      setSuccess(`Cumul bracelets corrigé : ${adults} ad / ${children} enf.`);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, 'Correction refusée.'));
    } finally {
      setBraceletSaving(false);
    }
  }

  async function handleCancelPayment(payment) {
    if (!window.confirm(`Annuler ce paiement de ${formatEur(payment.amount_cents)} ?`)) return;
    try {
      await cancelPayment(token, payment.id);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, "Annulation refusée."));
    }
  }

  const fullyPaid = isConfirmed && balance === 0 && paid > 0;
  const activePayments = payments.filter((p) => !p.cancelled_at);
  const hasPaidMeals = (row.paid_adults ?? 0) + (row.paid_children ?? 0) > 0;
  const braceletsPending = braceletsRemainAdults + braceletsRemainChildren > 0;

  const focusBracelets = stationMode === 'bracelets';
  const focusPayment = stationMode === 'encaissement';

  return (
    <Stack spacing={1.5}>
      {/* En-tête identité : nom à gauche, statut à droite */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {row.first_name} {row.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatReservationEmailDisplay(row.email)} · {row.phone}
          </Typography>
        </Box>
        <Typography
          variant="overline"
          color="primary.main"
          sx={{ flexShrink: 0, textAlign: 'right', lineHeight: 1.3, pt: 0.25 }}
        >
          {isConfirmed ? 'Réservation confirmée' : 'Préréservation'}
        </Typography>
      </Stack>

      {focusBracelets && braceletsPending && (
        <Alert severity="info" icon={<WristIcon />}>
          Poste <strong>Bracelets</strong> — remettez les bracelets pour les repas déjà payés.
        </Alert>
      )}
      {focusPayment && !fullyPaid && (
        <Alert severity="info" icon={<PaidOutlinedIcon />}>
          Poste <strong>Encaissement</strong> — enregistrez le paiement, puis passez les bracelets.
        </Alert>
      )}
      {stationMode === 'combo' && (braceletsPending || !fullyPaid) && (
        <Alert severity="info" icon={<PaidOutlinedIcon />}>
          Poste <strong>Encaissement + bracelets</strong> — traitez paiement et remise sur la même fiche.
        </Alert>
      )}

      {/* Compteurs : adultes / enfants payés vs réservés */}
      <Box
        sx={(theme) => ({
          p: 1.25,
          borderRadius: 1,
          bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
        })}
      >
        <CounterBox
          label="Adultes payés"
          value={`${row.paid_adults ?? 0} / ${row.adults}`}
          ok={(row.paid_adults ?? 0) >= row.adults}
        />
        <CounterBox
          label="Enfants payés"
          value={`${row.paid_children ?? 0} / ${row.children}`}
          ok={(row.paid_children ?? 0) >= row.children}
        />
        <CounterBox
          label="Solde"
          value={formatEur(balance)}
          ok={fullyPaid}
          color={balance > 0 ? 'warning.main' : 'success.main'}
        />
      </Box>

      {row.comment && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          <Typography variant="caption">{row.comment}</Typography>
        </Alert>
      )}

      <Stack
        spacing={1.5}
        sx={{
          display: 'flex',
          flexDirection: focusBracelets ? 'column-reverse' : 'column',
        }}
      >
      {/* Formulaire d'encaissement */}
      {!fullyPaid && (
        <Box>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Nouvel encaissement</Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Stepper
                  label="Adultes"
                  value={addAdults}
                  setValue={setAddAdults}
                  max={remainAdults}
                  unitCents={row.adult_cents ?? 0}
                />
                <Stepper
                  label="Enfants"
                  value={addChildren}
                  setValue={setAddChildren}
                  max={remainChildren}
                  unitCents={row.child_cents ?? 0}
                />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setAddAdults(remainAdults); setAddChildren(remainChildren); }}
                  disabled={remainAdults === 0 && remainChildren === 0}
                >
                  Solder ({formatEur(remainAdults * (row.adult_cents ?? 0) + remainChildren * (row.child_cents ?? 0))})
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { setAddAdults(0); setAddChildren(0); setOverrideEur(''); }}
                >
                  Réinit.
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label={`Montant ${overrideEur.trim() === '' ? '(calculé)' : '(forcé)'}`}
                  size="small"
                  type="number"
                  value={overrideEur === '' ? (computedCents / 100).toFixed(2) : overrideEur}
                  onChange={(e) => setOverrideEur(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  inputProps={{ step: '0.01', min: '0', inputMode: 'decimal' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  sx={{ flex: 1 }}
                  helperText={
                    overrideEur.trim() !== '' && Math.abs(effectiveCents - computedCents) > 0
                      ? `Montant calculé : ${formatEur(computedCents)}`
                      : ' '
                  }
                />
                <TextField
                  select
                  label="Méthode"
                  size="small"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  sx={{ flex: 1 }}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Note (optionnel)"
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                inputProps={{ maxLength: 200 }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<PaidOutlinedIcon />}
                disabled={saving || (addAdults === 0 && addChildren === 0)}
              >
                {saving
                  ? 'Enregistrement…'
                  : `Encaisser ${formatEur(effectiveCents)} (${addAdults} ad / ${addChildren} enf)`}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Remise bracelets */}
      {hasPaidMeals && (
        <Box>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Bracelets</Typography>
            <Button
              size="small"
              variant="text"
              onClick={handleCorrectBracelets}
              disabled={braceletSaving}
            >
              Corriger le cumul
            </Button>
          </Stack>
          <Box
            sx={(theme) => ({
              p: 1.25,
              borderRadius: 1,
              bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
            })}
          >
            <CounterBox
              label="Ad. remis"
              value={`${row.bracelets_adults_given ?? 0} / ${row.paid_adults ?? 0}`}
              ok={(row.bracelets_adults_given ?? 0) >= (row.paid_adults ?? 0) && (row.paid_adults ?? 0) > 0}
            />
            <CounterBox
              label="Enf. remis"
              value={`${row.bracelets_children_given ?? 0} / ${row.paid_children ?? 0}`}
              ok={(row.bracelets_children_given ?? 0) >= (row.paid_children ?? 0) && (row.paid_children ?? 0) > 0}
            />
            <CounterBox
              label="Reste à remettre"
              value={`${braceletsRemainAdults} ad / ${braceletsRemainChildren} enf`}
              ok={!braceletsPending}
              color={braceletsPending ? 'warning.main' : 'success.main'}
            />
          </Box>
          {braceletsPending ? (
            <Box component="form" onSubmit={handleHandoffBracelets}>
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Stepper
                    label="Bracelets ad."
                    value={giveAdults}
                    setValue={setGiveAdults}
                    max={braceletsRemainAdults}
                    unitCents={0}
                  />
                  <Stepper
                    label="Bracelets enf."
                    value={giveChildren}
                    setValue={setGiveChildren}
                    max={braceletsRemainChildren}
                    unitCents={0}
                  />
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setGiveAdults(braceletsRemainAdults);
                      setGiveChildren(braceletsRemainChildren);
                    }}
                  >
                    Tout le reste
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => { setGiveAdults(0); setGiveChildren(0); }}
                  >
                    Réinit.
                  </Button>
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<WristIcon />}
                  disabled={braceletSaving || (giveAdults === 0 && giveChildren === 0)}
                >
                  {braceletSaving
                    ? 'Enregistrement…'
                    : `Remettre ${giveAdults} ad / ${giveChildren} enf`}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Alert severity="success" variant="outlined">
              Tous les bracelets correspondant aux repas payés ont été remis.
            </Alert>
          )}
        </Box>
      )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {/* Historique des paiements */}
      <Divider />
      <Typography variant="subtitle2">
        Historique ({activePayments.length} encaissement{activePayments.length > 1 ? 's' : ''})
      </Typography>
      {payments.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          Aucun encaissement enregistré pour l&apos;instant.
        </Typography>
      ) : (
        <Stack divider={<Divider />} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {payments.map((p) => (
            <PaymentLine
              key={p.id}
              p={p}
              onCancel={() => handleCancelPayment(p)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Bloc statistique compact dans le bandeau des compteurs. */
function CounterBox({ label, value, ok, color }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: color ?? (ok ? 'success.main' : 'text.primary'),
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Stepper +/- avec libellé et coût unitaire calculé en dessous. */
function Stepper({ label, value, setValue, max, unitCents }) {
  const showPrice = typeof unitCents === 'number' && unitCents > 0;
  return (
    <Box
      sx={{
        flex: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {showPrice ? `${label} · ${formatEur(unitCents)}` : label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          onClick={() => setValue(Math.max(0, value - 1))}
          disabled={value === 0}
          aria-label={`Retirer 1 ${label}`}
        >
          <RemoveIcon />
        </IconButton>
        <Typography variant="h5" sx={{ minWidth: 36, textAlign: 'center', fontWeight: 700 }}>
          {value}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setValue(Math.min(max, value + 1))}
          disabled={value >= max}
          aria-label={`Ajouter 1 ${label}`}
        >
          <AddIcon />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        / {max} restant{max > 1 ? 's' : ''}
      </Typography>
    </Box>
  );
}

/** Une ligne d'historique de paiement (avec annulation). */
function PaymentLine({ p, onCancel }) {
  const cancelled = Boolean(p.cancelled_at);
  return (
    <Box
      sx={{
        p: 1,
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 1,
        alignItems: 'center',
        opacity: cancelled ? 0.5 : 1,
        textDecoration: cancelled ? 'line-through' : 'none',
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatEur(p.amount_cents)} · {p.adults} ad / {p.children} enf
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDateTime(p.created_at)} · {METHOD_LABEL[p.payment_method] ?? p.payment_method}
          {p.payment_notes ? ` · ${p.payment_notes}` : ''}
          {cancelled ? ` · annulé ${formatDateTime(p.cancelled_at)}` : ''}
        </Typography>
      </Box>
      {!cancelled && (
        <Tooltip title="Annuler ce paiement">
          <IconButton size="small" onClick={onCancel} aria-label="Annuler ce paiement">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
